import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/** @jsx jsx */

import { useCallback, useState } from 'react';
import { css, jsx } from '@emotion/core';
import Button from '@atlaskit/button/custom-theme-button';
import InlineDialog from '@atlaskit/inline-dialog';
import * as colors from '@atlaskit/theme/colors';
import { themed, useGlobalTheme } from '@atlaskit/theme/components';
import { itemSpacing } from '../../constants';
import MessageIcon from '../message-icon';
var buttonContentsStyles = css({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none'
});
var titleStyles = css({
  padding: "0 ".concat(itemSpacing, "px"),
  fontWeight: 500
});
var textStyles = css({
  padding: "0 ".concat(itemSpacing, "px"),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
var rootStyles = css({
  display: 'inline-block',
  maxWidth: '100%',
  '&:focus': {
    outline: '1px solid'
  },
  '&:hover': {
    // eslint-disable-next-line @repo/internal/styles/no-nested-styles
    '[data-ds--inline-message--icon]': {
      color: 'var(--icon-accent-color)'
    },
    // eslint-disable-next-line @repo/internal/styles/no-nested-styles
    '[data-ds--inline-message--button]': {
      textDecoration: 'underline'
    }
  }
});
var titleColor = themed({
  light: "var(--ds-text-highEmphasis, ".concat(colors.N600, ")"),
  dark: "var(--ds-text-highEmphasis, ".concat(colors.DN600, ")")
});
var textColor = themed({
  light: "var(--ds-text-lowEmphasis, ".concat(colors.N300, ")"),
  dark: "var(--ds-text-lowEmphasis, ".concat(colors.DN100, ")")
});
var iconColor = themed('appearance', {
  connectivity: {
    light: "var(--ds-iconBorder-brand, ".concat(colors.B300, ")"),
    dark: "var(--ds-iconBorder-brand, ".concat(colors.B75, ")")
  },
  confirmation: {
    light: "var(--ds-iconBorder-success, ".concat(colors.G200, ")"),
    dark: "var(--ds-iconBorder-success, ".concat(colors.G200, ")")
  },
  info: {
    light: "var(--ds-iconBorder-discovery, ".concat(colors.P200, ")"),
    dark: "var(--ds-iconBorder-discovery, ".concat(colors.P200, ")")
  },
  warning: {
    light: "var(--ds-iconBorder-warning, ".concat(colors.Y200, ")"),
    dark: "var(--ds-iconBorder-warning, ".concat(colors.Y200, ")")
  },
  error: {
    light: "var(--ds-iconBorder-danger, ".concat(colors.R300, ")"),
    dark: "var(--ds-iconBorder-danger, ".concat(colors.R300, ")")
  }
});
/**
 * __Inline message__
 *
 * An inline message lets users know when important information is available or when an action is required.
 *
 * - [Examples](https://atlassian.design/components/inline-message/examples)
 * - [Code](https://atlassian.design/components/inline-message/code)
 * - [Usage](https://atlassian.design/components/inline-message/usage)
 *
 * @example
 *
 * ```jsx
 * const Component = () => (
 *   <InlineMessage
 *    title="Inline Message Title Example"
 *    secondaryText="Secondary Text"
 *   >
 *    <p>Some text that would be inside the open dialog and otherwise hidden.</p>
 *   </InlineMessage>
 * );
 * ```
 */

var InlineMessage = function InlineMessage(_ref) {
  var _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom-start' : _ref$placement,
      _ref$secondaryText = _ref.secondaryText,
      secondaryText = _ref$secondaryText === void 0 ? '' : _ref$secondaryText,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'connectivity' : _ref$type,
      children = _ref.children,
      testId = _ref.testId,
      iconLabel = _ref.iconLabel;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var toggleDialog = useCallback(function () {
    setIsOpen(function (oldState) {
      return !oldState;
    });
  }, [setIsOpen]);
  var onCloseDialog = useCallback(function () {
    return setIsOpen(false);
  }, [setIsOpen]);
  var theme = useGlobalTheme();
  return jsx("div", {
    css: rootStyles,
    style: {
      '--icon-accent-color': iconColor({
        appearance: type,
        theme: theme
      })
    },
    "data-testid": testId
  }, jsx(InlineDialog, {
    onClose: onCloseDialog,
    content: children,
    isOpen: isOpen,
    placement: placement,
    testId: testId && "".concat(testId, "--inline-dialog")
  }, jsx(Button, {
    "data-ds--inline-message--button": true,
    appearance: "subtle-link",
    onClick: toggleDialog,
    spacing: "none",
    testId: testId && "".concat(testId, "--button")
  }, jsx("div", {
    css: buttonContentsStyles
  }, jsx(MessageIcon, {
    isOpen: isOpen,
    appearance: type,
    label: iconLabel
  }), title && jsx("span", {
    style: {
      color: titleColor({
        theme: theme
      })
    },
    css: titleStyles,
    "data-testid": testId && "".concat(testId, "--title")
  }, title), secondaryText && jsx("span", {
    style: {
      color: textColor({
        theme: theme
      })
    },
    css: textStyles,
    "data-testid": testId && "".concat(testId, "--text")
  }, secondaryText)))));
};

export default InlineMessage;